import {ArrowDropDown as ArrowDropDownIcon} from '@mui/icons-material';
import {
  Button,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
} from '@mui/material';
import {bool, node, oneOf, oneOfType, string} from 'prop-types';
import React, {useState} from 'react';

import {APPS, ENVIRONMENTS} from '../apps.jsx';

const MAIN_APPS = ['welcomePage', 'account', 'data', 'analyze'];

const SECONDARY_APPS = [
  'explorer',
  'featuresManager',
  'tasking',
  'basemapsViewer',
  'feedsViewer',
  'stories',
];

const APP_KEYS = [...MAIN_APPS, ...SECONDARY_APPS];

const AppDropdownButton = ({label, active, ...props}) => (
  <Button
    disableElevation
    disableRipple
    color="inherit"
    endIcon={<ArrowDropDownIcon />}
    sx={theme => ({
      ...theme.typography.subtitle2,
      textTransform: 'none',
    })}
    {...props}
  >
    {label}
  </Button>
);

AppDropdownButton.propTypes = {
  label: oneOfType([string, node]).isRequired,
  active: bool,
};
/**
 * Menu with links to Planet web applications. Used in `NavBar` component, but can be imported as a standalone component as well.
 */
const AppsDropdown = ({appKey, environment = ENVIRONMENTS.production}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const currentApp = APPS[appKey];

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <AppDropdownButton
        onClick={handleClick}
        label={
          <Stack direction="row" sx={{gap: 1, alignItems: 'center'}}>
            {currentApp?.logo && currentApp?.logo(20)}
            {currentApp.title}
          </Stack>
        }
        active={Boolean(anchorEl)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        keepMounted
        elevation={10}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              paddingX: 1,
              borderRadius: 1,
              marginTop: 1,
              minWidth: 200,
            },
          },
        }}
      >
        {MAIN_APPS.filter(id => APPS[id].href[environment]).map(id => {
          const app = APPS[id];

          return (
            <ListItemButton
              key={id}
              rel="noreferrer noopener"
              href={app.href[environment]}
              onClick={handleClose}
              sx={{borderRadius: 1}}
              selected={appKey === id}
              dense={!app.icon}
            >
              {app.icon && (
                <ListItemIcon
                  sx={{
                    color: 'inherit',
                    minWidth: theme => theme.spacing(5),
                  }}
                >
                  {app.logo()}
                </ListItemIcon>
              )}
              <ListItemText>{app.title}</ListItemText>
            </ListItemButton>
          );
        })}

        {SECONDARY_APPS.filter(id => APPS[id].href[environment]).map(id => {
          const app = APPS[id];

          return (
            <ListItemButton
              key={id}
              rel="noreferrer noopener"
              href={app.href[environment]}
              onClick={handleClose}
              sx={{borderRadius: 1}}
              dense
              selected={appKey === id}
            >
              <ListItemText>{app.title}</ListItemText>
            </ListItemButton>
          );
        })}
      </Menu>
    </>
  );
};

AppsDropdown.propTypes = {
  appKey: oneOf(APP_KEYS).isRequired,

  /**
   * The environment to use for the app links
   */
  environment: oneOf(Object.values(ENVIRONMENTS)),
};

export default AppsDropdown;
